import * as React from 'react';
import type {
  CommissionStatementMappingData,
  CommissionStatementMappingFieldName,
} from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { CommissionStatementMappingRepository } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { SpreadsheetColumnMap } from '../../Utilities/SpreadsheetColumnMap';

export type SpreadsheetDataRow = Array<any>;
export type SpreadsheetDataList = Array<SpreadsheetDataRow>;

export interface SpreadsheetProps {
  data: SpreadsheetDataList;
  dataFormatted?: SpreadsheetDataList;
  headerRow: number;
  ignoredRows: Array<number>;
  maxRows?: number;
  onDataChange: (
    value: Array<CommissionStatementMappingData> | null
  ) => Array<CommissionStatementMappingData | null>;
  onIgnoredRowsChange: (value: Array<number> | null) => Array<number>;
  value: Array<CommissionStatementMappingData> | null;
}

export const SpreadsheetMapper = (props: SpreadsheetProps): React.JSX.Element => {
  const [spreadsheetData, setSpreadsheetData] = React.useState<SpreadsheetDataList>(null);
  const [spreadsheetDataFormatted, setSpreadsheetDataFormatted] =
    React.useState<SpreadsheetDataList>(null);

  const [data, setData] = React.useState<Array<CommissionStatementMappingData> | null>(props.value);
  const [dataError, setDataError] = React.useState<string>();
  const [ignoredRows, setIgnoredRows] = React.useState<Array<number> | null>(props.ignoredRows);
  const [headerRow, setHeaderRow] = React.useState<number | null>(props.headerRow);

  const mappingRepository = new CommissionStatementMappingRepository();
  const fields = mappingRepository.getFields();
  const columns = SpreadsheetColumnMap.getColumns();

  const handleChangeField = (index: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldValue = event.target.value as CommissionStatementMappingFieldName;
    const newData = data ? [...data] : [];

    // Delete this index
    if (event.target.value === '0') {
      const existingIndex = newData.findIndex((newDataSingle) => newDataSingle.column === index);

      if (existingIndex >= 0) {
        newData.splice(existingIndex, 1);
      }
    } else {
      const existingValue = newData.find((newDataSingle) => newDataSingle.column === index);

      if (existingValue) {
        existingValue['field'] = fieldValue;
      } else {
        newData.push({
          column: index,
          field: fieldValue,
        });
      }
    }

    setData(newData);
    props.onDataChange(newData);
  };

  const handleClickRow = (rowIndex: number): void => {
    // Determine if the clicked row is the header
    const clickedRowIsHeader = headerRow >= 0 && rowIndex === headerRow;

    // Providing this row isn't the header, add it to the ignore list
    if (clickedRowIsHeader === false) {
      const newIgnoredRows = ignoredRows && ignoredRows.length > 0 ? [...ignoredRows] : [];
      const existingRowIndex = newIgnoredRows.indexOf(rowIndex);

      if (existingRowIndex >= 0) {
        newIgnoredRows.splice(existingRowIndex, 1);
      } else {
        newIgnoredRows.push(rowIndex);
      }

      setIgnoredRows(newIgnoredRows);
      props.onIgnoredRowsChange(newIgnoredRows);
    }
  };

  React.useEffect(() => {
    setDataError(null);

    try {
      mappingRepository.validateFields(data);
    } catch (e) {
      setDataError(e.message);
    }
  }, [data]);

  React.useEffect(() => {
    setData(props.value);
  }, [props.value]);

  React.useEffect(() => {
    setIgnoredRows(props.ignoredRows);
  }, [props.ignoredRows]);

  React.useEffect(() => {
    if (props.headerRow) {
      const newIgnoredRows = ignoredRows && ignoredRows.length > 0 ? [...ignoredRows] : [];
      const existingRowIndex = newIgnoredRows.indexOf(props.headerRow);

      if (existingRowIndex >= 0) {
        newIgnoredRows.splice(existingRowIndex, 1);
        setIgnoredRows(newIgnoredRows);
        props.onIgnoredRowsChange(props.ignoredRows);
      }
    }

    setHeaderRow(props.headerRow);
  }, [props.headerRow]);

  React.useEffect(() => {
    let newData = props.data ? [...props.data] : null;

    if (props.maxRows && newData) {
      newData = newData.splice(0, props.maxRows);
    }

    setSpreadsheetData(newData);
  }, [props.data]);

  React.useEffect(() => {
    let newDataFormatted =
      props.dataFormatted && Array.isArray(props.dataFormatted) && props.dataFormatted.length
        ? [...props.dataFormatted]
        : null;

    if (props.maxRows && newDataFormatted) {
      newDataFormatted = newDataFormatted.splice(0, props.maxRows);
    }

    setSpreadsheetDataFormatted(newDataFormatted);
  }, [props.dataFormatted]);

  const activeColumns = data ? Array.from(data, (dataSingle) => dataSingle.column) : null;
  const spreadhSheetEmpty = !spreadsheetData || (spreadsheetData && spreadsheetData.length === 0);
  const selectedSpreadSheetData = spreadsheetDataFormatted
    ? spreadsheetDataFormatted
    : spreadsheetData;

  return (
    <div className="spreadsheet">
      {spreadhSheetEmpty && (
        <p className="alert alert-info small">No spreadsheet data or invalid data provided</p>
      )}
      {!spreadhSheetEmpty && dataError && <p className="alert alert-info small">{dataError}</p>}
      {selectedSpreadSheetData && selectedSpreadSheetData.length > 0 && (
        <table>
          <thead>
            <tr>
              {selectedSpreadSheetData[0] &&
                selectedSpreadSheetData[0].map((dataField, dataFieldIndex) => (
                  <th className="field" key={dataFieldIndex}>
                    <select
                      className="form-control"
                      onChange={handleChangeField(dataFieldIndex)}
                      name="column"
                    >
                      <option value={0}>?</option>
                      {fields.map((field) => {
                        const fieldExists =
                          data &&
                          data.find((dataSingle) => dataSingle.field === field.name) !== undefined;
                        const selected =
                          data &&
                          data.find(
                            (dataSingle) =>
                              dataSingle.column === dataFieldIndex &&
                              dataSingle.field === field.name
                          ) !== undefined;

                        return (
                          <option
                            disabled={fieldExists && !selected}
                            selected={selected}
                            value={field.name}
                            key={field.name}
                          >
                            {field.label}
                          </option>
                        );
                      })}
                    </select>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {selectedSpreadSheetData &&
              selectedSpreadSheetData.map((dataSingle, dataSingleIndex) => (
                <tr key={dataSingleIndex} onClick={(e) => handleClickRow(dataSingleIndex)}>
                  {dataSingle.map((dataSingleField, dataSingleFieldIndex) => {
                    const classNames = [];

                    if (activeColumns && activeColumns.includes(dataSingleFieldIndex)) {
                      classNames.push('active');
                    }

                    if (dataSingleIndex === headerRow) {
                      classNames.push('header');
                    }

                    if (ignoredRows && ignoredRows.includes(dataSingleIndex)) {
                      classNames.push('ignored');
                    }

                    return (
                      <td
                        key={dataSingleFieldIndex}
                        title="Click to ignore this row"
                        className={classNames.join(' ')}
                      >
                        {dataSingleField || <>&nbsp;</>}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
